import { IconType } from "react-icons";
import { MdOutlinePayments } from "react-icons/md";

import { IoDocumentTextOutline, IoLibraryOutline, IoRibbonOutline } from "react-icons/io5";
import { FaRegMoneyBill1 } from "react-icons/fa6";
import { AiOutlineSignature } from "react-icons/ai";
import { useLocation, useNavigate } from "react-router-dom"

import { HStack, Icon, Image, Spacer, Stack, VStack } from "@chakra-ui/react"
import { NAV_OPTIONS, NAV_OPTIONS_NO_DEALS } from "@/constants"
import { useAuth } from "@/contexts"
import { Deal, DealManager, DealStep, NavOption } from "@/types"
import { Button, PulsingDot, Text } from "../ui"

const NavItem = ({ label, isActive }: { label: string; isActive: boolean }) => {
  const navigate = useNavigate()

  const convertUnderscoresToSpaces = (str: string) => {
    if (str === NavOption.TAX_CALCULATION) {
      return "Calculate Your Taxes";
    }
    return str
    .split("_")
    .map(
      word => word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(" ");
  }

  const getIconForLabel = (label: string): IconType => {
    switch(label) {
      case NavOption.DOCUMENTS:
        return IoDocumentTextOutline;
      case NavOption.COMPLIANCE:
        return IoRibbonOutline;
      case NavOption.CURRENT_OFFER:
        return AiOutlineSignature;
      case NavOption.TAX_CALCULATION:
        return FaRegMoneyBill1;
      case NavOption.RESOURCES:
        return IoLibraryOutline;
      case NavOption.PAYMENT_CENTER:
        return MdOutlinePayments;
      default:
        return IoDocumentTextOutline;
    }
  }

  return (
    <Stack
      p={3}
      backgroundColor={isActive ? "whiteAlpha.100" : "transparent"}
      cursor="pointer"
      _hover={{ backgroundColor: "whiteAlpha.100" }}
      onClick={() => navigate(`/client/${label === NavOption.TAX_CALCULATION ? "taxes" : label === NavOption.CURRENT_OFFER ? "deals" : label === NavOption.PAYMENT_CENTER ? "payment" : label}`)}
    >
      <HStack>
        <Icon as={getIconForLabel(label) as React.ElementType} size="sm" color={isActive ? "white" : ""} />
        <Text fontSize="sm" fontWeight="medium" color={isActive ? "white" : "#B9BBBE"}>
          {convertUnderscoresToSpaces(label)}
        </Text>
        {
          label === NavOption.COMPLIANCE ? <><Spacer /><PulsingDot /></> : null
        }
      </HStack>
    </Stack>
  )
}

interface Props {
  deals: Deal[]
  dealManager?: DealManager
}

export const Navigation = ({ deals, dealManager }: Props) => {
  const location = useLocation()
  const checkIsCurrentNavOption = (option: string) => {
    if (option === NavOption.TAX_CALCULATION) {
      return location.pathname === "/taxes"
    }

    if (option === NavOption.CURRENT_OFFER) {
      return location.pathname === "/deals"
    }
    
    if (option === NavOption.PAYMENT_CENTER) {
      return location.pathname === "/client/payment"
    }

    return location.pathname === `/${option}`
  }

  const { user } = useAuth()

  // ── Test override: set to 1 to force Payment Center for test email ──
  const PAYMENT_CENTER_TEST_MODE = 1
  const PAYMENT_CENTER_TEST_EMAIL = "williamskyu@gmail.com"

  const isTestOverride = PAYMENT_CENTER_TEST_MODE === 1
    && user?.email?.toLowerCase().trim() === PAYMENT_CENTER_TEST_EMAIL

  const showPaymentCenter = isTestOverride
    || deals?.some(deal => deal.portalStage === DealStep.SEND_WIRE_INFO)

  const baseNavOptions = deals && deals.length > 0 ? NAV_OPTIONS : NAV_OPTIONS_NO_DEALS
  const navOptionsToRender = showPaymentCenter
    ? [...baseNavOptions, NavOption.PAYMENT_CENTER]
    : baseNavOptions
  const safeNavOptions = navOptionsToRender.filter(Boolean)

  return (
    <Stack h="100%" bg="sidebar.bg" color="white" pb={10}>
      <Text fontSize="xl" fontWeight="bold" pt={5} pb={5} pl={5} fontFamily={"body"}>
        ESO Fund Client Portal
      </Text>
      {navOptionsToRender.map((option) => (
        <NavItem key={option} label={option} isActive={checkIsCurrentNavOption(option)} />
      ))}
      <Spacer />
      {dealManager && (
        <VStack borderRadius={10} bg="whiteAlpha.100" p={5} ml={5} mr={5}>
          <Text fontSize="2xs" textAlign="center">
            Your Equity Partner
          </Text>
          {dealManager.profilePicture && (
            <Image
              src={dealManager.profilePicture}
              borderRadius={10}
              boxSize="90px"
              alignSelf="center"
            />
          )}
          <Text fontSize="lg" fontWeight="bold" mt={3} textAlign="center">
            { dealManager.name }
          </Text>
          <Text fontSize="xs" textAlign="center">
            { dealManager.email }
          </Text>
          {dealManager.calendly && (
            <Button
              variant="outline"
              color="white"
              size="sm"
              mt={3}
              w="100%"
              onClick={() => window.open(dealManager.calendly!, "_blank")}
              _hover={{ bg: "whiteAlpha.200" }}
            >
              Schedule Call
            </Button>
          )}
        </VStack>
      )}
    </Stack>
  )
}